import React from 'react'
import BaseField from './BaseField'
import './FieldNumberRange.scss'


export default function FieldNumberRange(props) {
  /* Input Number
   * Expected Props:
   * className = <string> Optional
   * label = <string> Required
   * required = <boolean> Optional
   * value = <string> Required
   * value2 = <string> Required
   * placeholder = <string> Optional
   * valid = <boolean> Optional
   * validMessage = <string> Optional
   * invalidMessage = <string> Optional
   * help = <string> Optional
   * helpAboveInput = <boolean> Optional
   * onChange = <function()> Required
   * onChange2 = <function()> Required
   * onBlur = <function()> Optional
   * onBlur2 = <function()> Optional
   * disabled = <boolean> Optional
   * readOnly = <boolean> Optional
   * prepend = <string> Optional
   * append = <string> Optional
   * step = <string> Optional
   * min = <integer> Optional
   * max = <integer> Optional
   * horizontal = <boolean> Optional
   * horizontalLabelSize = <int [1-23]> Optional (Default = 6)
   */
  const value = props.value || ""
  const value2 = props.value2 || ""
  const minValue = isNaN(props.min) || props.min === null ? "" : props.min
  const maxValue = isNaN(props.max) || props.max === null ? value2 : Math.min(props.max, value2)
  const minValue2 = isNaN(props.min) || props.min === null ? value : Math.max(props.min, value)
  const maxValue2 = isNaN(props.max) || props.max === null ? "" : props.max

  // EFFECTS
  React.useEffect(() => {
    if (value !== "" && minValue !== "" && Number(value) < Number(minValue)) {
      const delayedChange = setTimeout(() => props.onChange({ target: { value: minValue } }), 500)
      return () => clearTimeout(delayedChange)
    } else if (value !== "" && maxValue !== "" && Number(value) > Number(maxValue)) {
      const delayedChange = setTimeout(() => props.onChange({ target: { value: maxValue } }), 500)
      return () => clearTimeout(delayedChange)
    }
  }, [value, props.min, props.max])

  React.useEffect(() => {
    if (value2 !== "" && minValue2 !== "" && Number(value2) < Number(minValue2)) {
      const delayedChange = setTimeout(() => props.onChange2({ target: { value: minValue2 } }), 500)
      return () => clearTimeout(delayedChange)
    } else if (value2 !== "" && maxValue2 !== "" && Number(value2) > Number(maxValue2)) {
      const delayedChange = setTimeout(() => props.onChange2({ target: { value: maxValue2 } }), 500)
      return () => clearTimeout(delayedChange)
    }
  }, [value2, props.min, props.max])

  // FUNCTIONS
  const onChange = event => {
    // Process changes ONLY if valid (ignoring max/min values)
    if (event.target.validity.valid || event.target.validity.rangeUnderflow || event.target.validity.rangeOverflow) {
      props.onChange(event)
    }
  }

  const onChange2 = event => {
    // Process changes ONLY if valid (ignoring max/min values)
    if (event.target.validity.valid || event.target.validity.rangeUnderflow || event.target.validity.rangeOverflow) {
      props.onChange2(event)
    }
  }

  const onInput = event => {
    // If NOT valid
    if (!event.target.validity.valid) {
      // If bad value is saved to state or trying to empty a required field
      if (event.target.value === value || event.target.validity.valueMissing) {
        // Set value as empty string
        event.target.value = ""
        props.onChange(event)
      }
    }
  }

  const onInput2 = event => {
    // If NOT valid
    if (!event.target.validity.valid) {
      // If bad value is saved to state or trying to empty a required field
      if (event.target.value === value2 || event.target.validity.valueMissing) {
        // Set value as empty string
        event.target.value = ""
        props.onChange2(event)
      }
    }
  }

  // RENDER
  return (
    <BaseField
      className={props.className}
      labelClassName={props.labelClassName}
      inputClassName={props.inputClassName}
      label={props.label}
      input={
        <div className="input-group field-number-range">
          {props.prepend && (
            <div className="input-group-prepend">
              <span className="input-group-text">{props.prepend}</span>
            </div>
          )}
          <input
            ref={props.inputRef}
            className={"form-control custom-field-input" + (
              props.valid === true ? " is-valid" : props.valid === false ? " is-invalid" : ""
            )}
            name={props.name}
            type="number"
            placeholder={props.placeholder || "min"}
            value={value}
            required={props.required && props.valid === false}
            onChange={onChange}
            onInput={onInput}
            onBlur={props.onBlur}
            disabled={props.disabled}
            readOnly={props.readOnly}
            autoFocus={props.autoFocus}
            step={props.step}
            min={minValue}
            max={maxValue}
          />
          <span className="form-control custom-field-input field-number-range-separator">~</span>
          <input
            className={"form-control custom-field-input" + (
              props.valid === true ? " is-valid" : props.valid === false ? " is-invalid" : ""
            )}
            name={props.name}
            type="number"
            placeholder={props.placeholder2 || props.placeholder || "max"}
            value={value2}
            required={props.required && props.valid === false}
            onChange={onChange2}
            onInput={onInput2}
            onBlur={props.onBlur2}
            disabled={props.disabled}
            readOnly={props.readOnly}
            step={props.step}
            min={minValue2}
            max={maxValue2}
          />
          {props.append && (
            <div className="input-group-append">
              <span className="input-group-text">{props.append}</span>
            </div>
          )}
        </div>
      }
      disabled={props.disabled}
      readOnly={props.readOnly}
      required={props.required}
      valid={props.valid}
      validMessage={props.validMessage}
      invalidMessage={props.invalidMessage}
      help={props.help}
      helpAboveInput={props.helpAboveInput}
      horizontal={props.horizontal}
      horizontalLabelSize={props.horizontalLabelSize}
    />
  )
}
